import React, {useEffect, useRef, useState} from "react";
import {Button, Col, Input, InputGroup, InputGroupAddon, ListGroup, ListGroupItem, Row} from "reactstrap";
import {nomAppareil} from "../../../../util/assistantPec";
import configuration from "../../../../configuration/configuration";

const prix = (p) => (p === null || p === undefined || p === "") ? "" : `${String(p).replace(".", ",")} €`;

/** Étape 2 : l'appareil à réparer et sa ou ses prestations, avec création rapide si rien ne correspond. */
export default function EtapeAppareil({api, client, ligne, onChange, onErreur}) {
    const [recherche, setRecherche] = useState("");
    const [resultats, setResultats] = useState([]);
    const [chargement, setChargement] = useState(false);
    const [cherche, setCherche] = useState("");
    const [nouvelAppareil, setNouvelAppareil] = useState(null);
    const [nouvellePresta, setNouvellePresta] = useState(null);
    const [marques, setMarques] = useState([]);
    const champ = useRef(null);

    useEffect(() => {
        if (!ligne.appareil && champ.current) {
            champ.current.focus();
        }
    }, [ligne.appareil]);

    useEffect(() => {
        const texte = recherche.trim();
        if (texte.length < 2) {
            setResultats([]);
            return undefined;
        }
        let annule = false;
        const minuteur = setTimeout(async () => {
            setChargement(true);
            try {
                const trouves = await api.chercherAppareils(texte);
                if (!annule) {
                    setResultats(trouves);
                    setCherche(texte);
                }
            } catch (e) {
                if (!annule) {
                    setResultats([]);
                    setCherche(texte);
                }
            }
            if (!annule) {
                setChargement(false);
            }
        }, 250);
        return () => {
            annule = true;
            clearTimeout(minuteur);
        };
    }, [recherche, api]);

    const choisirAppareil = (appareil, connu = null) => {
        onChange({
            appareil,
            prestations: [],
            userAppareilId: connu?.pivot?.id ?? null,
            serial: connu?.pivot?.serial ?? "",
            code: connu?.pivot?.code ?? "",
        });
        setNouvelAppareil(null);
    };

    const reprendreMachine = async (machine) => {
        try {
            const appareil = await api.appareil(machine.id);
            choisirAppareil(appareil ?? {id: machine.id, nom: machine.nom, marque: machine.marque, prestations: []}, machine);
        } catch (e) {
            onErreur("Impossible de charger cet appareil.");
        }
    };

    const ouvrirCreationAppareil = async () => {
        setNouvelAppareil({nom: recherche.trim(), marque: ""});
        if (marques.length === 0) {
            try {
                setMarques(await api.marques());
            } catch (e) {
            }
        }
    };

    const creerAppareil = async () => {
        const marqueConnue = marques.find(m => m.nom.toLowerCase() === nouvelAppareil.marque.trim().toLowerCase());
        try {
            const cree = await api.creerAppareil({
                nom: nouvelAppareil.nom.trim(),
                annuaire_marque_id: marqueConnue?.id ?? null,
                marque: marqueConnue ? null : nouvelAppareil.marque.trim(),
            });
            choisirAppareil(cree);
            setNouvellePresta(cree.prestations.length === 0 ? {title: "", prix: ""} : null);
        } catch (e) {
            onErreur(e?.response?.data?.message || "Impossible de créer cet appareil.");
        }
    };

    const basculerPresta = (presta) => {
        const deja = ligne.prestations.some(p => p.id === presta.id);
        onChange({prestations: deja ? ligne.prestations.filter(p => p.id !== presta.id) : [...ligne.prestations, presta]});
    };

    const creerPresta = async () => {
        try {
            const cree = await api.creerPrestation({
                annuaire_appareil_id: ligne.appareil.id,
                title: nouvellePresta.title.trim(),
                global_price_mo: String(nouvellePresta.prix).replace(",", "."),
            });
            onChange({
                appareil: {...ligne.appareil, prestations: [...(ligne.appareil.prestations || []), cree]},
                prestations: [...ligne.prestations, cree],
            });
            setNouvellePresta(null);
        } catch (e) {
            onErreur(e?.response?.data?.message || "Impossible de créer cette prestation.");
        }
    };

    if (ligne.appareil) {
        const prestations = ligne.appareil.prestations || [];
        return (
            <div>
                <div className="assistant-pec-choix mb-4">
                    <div>
                        <div className="h3 mb-0">{nomAppareil(ligne.appareil)}</div>
                        {ligne.userAppareilId && <div className="text-muted small">Appareil déjà venu chez nous{ligne.serial ? ` (n° ${ligne.serial})` : ""}</div>}
                    </div>
                    <Button outline color="primary" onClick={() => onChange({appareil: null, prestations: [], userAppareilId: null})}>
                        Changer d'appareil
                    </Button>
                </div>

                <h4>Quelle réparation ?</h4>
                <p className="text-muted small mb-2">Plusieurs choix possibles. Sans prestation, l'appareil part en diagnostic.</p>
                <div className="assistant-pec-tuiles">
                    {prestations.map(p => {
                        const choisie = ligne.prestations.some(c => c.id === p.id);
                        return (
                            <button type="button" key={p.id}
                                    className={"assistant-pec-tuile assistant-pec-tuile-presta" + (choisie ? " choisie" : "")}
                                    onClick={() => basculerPresta(p)}>
                                <span className="assistant-pec-coche">{choisie ? <i className="fa fa-check"/> : null}</span>
                                {p.image ? (
                                    <img className="assistant-pec-miniature" alt="" src={configuration.API + p.image}
                                         onError={e => { e.currentTarget.style.visibility = "hidden"; }}/>
                                ) : <span className="assistant-pec-miniature assistant-pec-miniature-vide"><i className="fa fa-wrench"/></span>}
                                <span className="assistant-pec-tuile-texte">
                                    <span className="assistant-pec-tuile-titre">{p.title}</span>
                                    <span className="assistant-pec-tuile-prix">{prix(p.prix)}</span>
                                    {!p.publiee && <span className="assistant-pec-tuile-note">créée au comptoir</span>}
                                </span>
                            </button>
                        );
                    })}
                    {!nouvellePresta && (
                        <button type="button" className="assistant-pec-tuile assistant-pec-tuile-ajout"
                                onClick={() => setNouvellePresta({title: "", prix: ""})}>
                            <i className="fa fa-plus mr-2"/>Autre réparation
                        </button>
                    )}
                </div>

                {nouvellePresta && (
                    <Row className="mt-3 align-items-end">
                        <Col md={6}>
                            <label className="small text-muted mb-1">Nouvelle prestation pour {nomAppareil(ligne.appareil)}</label>
                            <Input autoFocus placeholder="Ex. : remplacement du joystick gauche" value={nouvellePresta.title}
                                   onChange={e => setNouvellePresta({...nouvellePresta, title: e.target.value})}/>
                        </Col>
                        <Col md={3}>
                            <InputGroup>
                                <Input type="number" min="0" step="0.01" placeholder="Prix" value={nouvellePresta.prix}
                                       onChange={e => setNouvellePresta({...nouvellePresta, prix: e.target.value})}/>
                                <InputGroupAddon addonType="append">€ TTC</InputGroupAddon>
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Button color="primary" disabled={!nouvellePresta.title.trim() || nouvellePresta.prix === ""}
                                    onClick={creerPresta}>Ajouter</Button>
                            <Button color="link" onClick={() => setNouvellePresta(null)}>Annuler</Button>
                        </Col>
                        <Col xs={12}><div className="text-muted small mt-1">Elle reste invisible sur le site, mais sera proposée ici la prochaine fois.</div></Col>
                    </Row>
                )}
            </div>
        );
    }

    const machines = client?.appareils ?? [];

    return (
        <div>
            {machines.length > 0 && (
                <div className="mb-4">
                    <h4>Un appareil déjà venu ?</h4>
                    <div className="assistant-pec-tuiles">
                        {machines.map(m => (
                            <button type="button" key={m.pivot?.id ?? m.id} className="assistant-pec-tuile"
                                    onClick={() => reprendreMachine(m)}>
                                <span className="assistant-pec-tuile-titre">{nomAppareil(m)}</span>
                                {m.pivot?.serial && <span className="assistant-pec-tuile-note">n° {m.pivot.serial}</span>}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <h4>{machines.length > 0 ? "Ou un autre appareil" : "Quel appareil ?"}</h4>
            <Input
                innerRef={champ}
                bsSize="lg"
                placeholder="Ex. : dualsense, switch oled, asus vivobook…"
                value={recherche}
                onChange={e => {
                    setRecherche(e.target.value);
                    setNouvelAppareil(null);
                }}
                onKeyDown={e => {
                    if (e.key === "Enter" && resultats.length > 0) {
                        choisirAppareil(resultats[0]);
                    }
                }}
            />
            {chargement && <div className="text-muted small mt-2">Recherche…</div>}
            {resultats.length > 0 && (
                <ListGroup className="mt-2">
                    {resultats.map(a => (
                        <ListGroupItem key={a.id} tag="button" action onClick={() => choisirAppareil(a)}>
                            <strong>{nomAppareil(a)}</strong>
                            <span className="float-right text-muted small">
                                {a.prestations.length} prestation{a.prestations.length > 1 ? "s" : ""}
                            </span>
                        </ListGroupItem>
                    ))}
                </ListGroup>
            )}

            {recherche.trim().length >= 2 && !chargement && !nouvelAppareil && (
                <div className="mt-3">
                    {resultats.length === 0 && cherche === recherche.trim() && <span className="text-muted mr-2">Rien ne correspond.</span>}
                    <Button outline color="success" onClick={ouvrirCreationAppareil}>
                        <i className="fa fa-plus mr-2"/>Créer l'appareil « {recherche.trim()} »
                    </Button>
                </div>
            )}

            {nouvelAppareil && (
                <Row className="mt-3 align-items-end">
                    <Col md={4}>
                        <label className="small text-muted mb-1">Marque</label>
                        <Input list="assistant-pec-marques" autoFocus placeholder="Ex. : Sony" value={nouvelAppareil.marque}
                               onChange={e => setNouvelAppareil({...nouvelAppareil, marque: e.target.value})}/>
                        <datalist id="assistant-pec-marques">
                            {marques.map(m => <option key={m.id} value={m.nom}/>)}
                        </datalist>
                        {nouvelAppareil.marque.trim() && marques.length > 0
                        && !marques.some(m => m.nom.toLowerCase() === nouvelAppareil.marque.trim().toLowerCase()) && (
                            <div className="small text-warning mt-1">Nouvelle marque : « {nouvelAppareil.marque.trim()} » sera créée</div>
                        )}
                    </Col>
                    <Col md={5}>
                        <label className="small text-muted mb-1">Modèle</label>
                        <Input value={nouvelAppareil.nom}
                               onChange={e => setNouvelAppareil({...nouvelAppareil, nom: e.target.value})}/>
                    </Col>
                    <Col md={3}>
                        <Button color="primary" disabled={!nouvelAppareil.nom.trim() || !nouvelAppareil.marque.trim()}
                                onClick={creerAppareil}>Créer</Button>
                        <Button color="link" onClick={() => setNouvelAppareil(null)}>Annuler</Button>
                    </Col>
                    <Col xs={12}><div className="text-muted small mt-1">Il est ajouté à l'annuaire sans être publié sur le site.</div></Col>
                </Row>
            )}
        </div>
    );
}
